import { Fragment, ReactNode, useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { TPopulatedForm } from "../../hooks/applications.hooks";
import {
  RenderDocumentConfigs,
  RenderField,
  RenderKhasaraManualInput,
  RenderKhasaraSelect,
} from "../RenderField/RenderField";
import { OfflineTentativeDetailFields } from "../OfflineTentativeDetails/OfflineTentativeDetails";
import { isOfflineTentativeTypeValue } from "../../config/phaseConstants";
import { branchFeatureFlags } from "../../config/branchFeatureFlags";
import {
  KHASARA_BLOCK_UIDS,
  PINNED_END_FIELD_UIDS,
  START_AFTER_KHASARA_BLOCK_UIDS,
  START_BEFORE_KHASARA_BLOCK_UIDS,
} from "../FieldSequence/applicantFormLayout";
import { sequenceStepLayout } from "../FieldSequence/fieldSequenceOrder";
import { useApplicantFormFieldSequence } from "../../hooks/field-sequence.hooks";

/**
 * BEFORE - the default layout lists were defined here. They moved, unchanged,
 * to FieldSequence/applicantFormLayout.ts, so the Field Sequence Admin screen
 * starts its list from the same default order this form draws:
 *
 * const START_BEFORE_KHASARA_BLOCK_UIDS = [
 *   "plot_owner_s_name",
 *   "unauthorized_layout_name",
 *   "name_of_service",
 *   "rl_application_type",
 *   "is_there_a_construction",
 *   "is_there_a_court_case_going_on_regarding_the_plot",
 * ];
 *
 * // Mauja / Khasara No. / Plot No. render together as one self-contained
 * // sub-grid (see below) instead of through the generic per-field loop below,
 * // because their desired mobile-stacking order (Manual Sub-Khasara glued
 * // directly under Khasara No.) differs from their desktop row-pairing
 * // (Plot No. + Manual Sub-Khasara sharing a row) -- two layouts that can't
 * // share one flat DOM order. Isolating them in their own grid container lets
 * // order-N / md:order-N apply safely, without touching the CSS order of any
 * // other (unrelated) field in the outer grid.
 * const KHASARA_BLOCK_UIDS = ["mauja", "khasara_no", "plot_no"];
 *
 * const START_AFTER_KHASARA_BLOCK_UIDS: string[] = [];
 *
 * const PINNED_END_FIELD_UIDS = [
 *   "email_id",
 *   "mobile_no",
 *   "address",
 *   "submitted_by",
 *   "type_of_application_submitter",
 * ];
 */

function MaujaKhasaraPlotBlock({
  maujaField,
  khasaraField,
  plotNoField,
  manualKhasaraField,
  disabled,
}: {
  maujaField: any;
  khasaraField: any;
  plotNoField: any;
  manualKhasaraField: any;
  disabled?: boolean;
}) {
  return (
    <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-10">
      <div className="order-1">
        <RenderField field={maujaField} disabled={disabled} />
      </div>
      <div className="order-2">
        <RenderKhasaraSelect field={khasaraField} disabled={disabled} />
      </div>
      <div className="order-3 md:order-3">
        <RenderField field={plotNoField} disabled={disabled} />
      </div>
      <div className="order-4 md:order-4">
        <RenderKhasaraManualInput khasaraFieldUid={khasaraField.uid} disabled={disabled} field={manualKhasaraField} />
      </div>
    </div>
  );
}

export function RenderStep({
  step,
  children,
  defaultValues,
  onChangeService,
}: {
  defaultValues?: Record<string, any>;
  children: (_: ReturnType<typeof useForm>) => ReactNode | ReactNode[];
  onChangeService: (service: number) => unknown;
  step: TPopulatedForm;
}) {
  const formMethods = useForm({
    defaultValues: { ...defaultValues },
    mode: "onChange",
    shouldFocusError: true,
  });

  const service = useWatch({
    name: "name_of_service",
    control: formMethods.control,
    defaultValue: Number(defaultValues?.name_of_service) || undefined,
  });

  useEffect(() => {
    onChangeService(service);
  }, [service]);

  /**
   * The Offline Tentative reference details (Demand Note / certificate number
   * and date) are shown on the step that holds the Application Type dropdown,
   * and only while that dropdown is set to Offline Tentative. They are
   * watched rather than read once so they appear and disappear as the
   * applicant changes the type.
   */
  const rlApplicationType = useWatch({
    name: "rl_application_type",
    control: formMethods.control,
    defaultValue: defaultValues?.rl_application_type,
  });
  const showOfflineTentativeDetails =
    branchFeatureFlags.ENABLE_OFFLINE_TENTATIVE_RL &&
    (step?.fields || []).some((f) => f.uid === "rl_application_type") &&
    isOfflineTentativeTypeValue(rlApplicationType);

  /**
   * The order the Admin set on the Field Sequence screen (fields and empty
   * spaces), or null while the step keeps its default layout (switch off,
   * nothing saved, or a municipality without the feature).
   */
  const { data: sequenceOrder } = useApplicantFormFieldSequence();

  const { formState } = formMethods;
  const { isValid } = formState;
  console.log("fields & document", step.fields, step.documentConfigs)

  // cp_form_field_mappings has no sort_order column, so fields render in
  // field-id order by default. These uids are pinned to a specific visual
  // position instead: start uids at the top (in this exact order), the
  // Mauja/Khasara/Plot No. trio right after as one block (see
  // MaujaKhasaraPlotBlock above), then whatever fields aren't pinned (in
  // their existing relative order), then end uids at the very bottom.
  const orderedLayout = (() => {
    let fields = step?.fields || [];
    let byUid = new Map(fields.map((f) => [f.uid, f]));

    const hasFullBlock = KHASARA_BLOCK_UIDS.every((uid) => byUid.has(uid));

    // Edge case: a service has khasara_no but not the full mauja/khasara/
    // plot-no trio, so it won't get MaujaKhasaraPlotBlock below. Keep
    // Manual Sub-Khasara next to it by injecting a synthetic field entry
    // (RenderField has a fallback branch for "manual_khasara_no" for
    // exactly this case) so it isn't silently dropped from the form.
    if (!hasFullBlock && byUid.has("khasara_no") && !byUid.has("manual_khasara_no")) {
      const khasaraField = byUid.get("khasara_no")!;
      const khasaraIndex = fields.findIndex((f) => f.uid === "khasara_no");
      const manualField = { ...khasaraField, uid: "manual_khasara_no", id: -1 };
      fields = [...fields];
      fields.splice(khasaraIndex + 1, 0, manualField);
      byUid = new Map(fields.map((f) => [f.uid, f]));
    }

    const pickPinned = (uids: string[]) =>
      uids.map((uid) => byUid.get(uid)).filter((f): f is (typeof fields)[number] => !!f);

    const startBefore = pickPinned(START_BEFORE_KHASARA_BLOCK_UIDS);
    const startAfter = pickPinned(START_AFTER_KHASARA_BLOCK_UIDS);
    const endFields = pickPinned(PINNED_END_FIELD_UIDS);

    // main_khasara_no/manual_khasara_no are absorbed into the block below
    // (khasaraField cell + the injected Manual Sub-Khasara cell) whenever a
    // service maps them as real fields alongside the full trio -- excluding
    // them here stops the generic per-field loop from rendering them again.
    const pinnedUids = new Set([
      ...START_BEFORE_KHASARA_BLOCK_UIDS,
      ...(hasFullBlock ? [...KHASARA_BLOCK_UIDS, "main_khasara_no", "manual_khasara_no"] : []),
      ...START_AFTER_KHASARA_BLOCK_UIDS,
      ...PINNED_END_FIELD_UIDS,
    ]);
    const middleFields = fields.filter((f) => !pinnedUids.has(f.uid));

    return {
      startBefore,
      block: hasFullBlock
        ? {
          maujaField: byUid.get("mauja"),
          khasaraField: byUid.get("khasara_no"),
          plotNoField: byUid.get("plot_no"),
          manualKhasaraField: byUid.get("manual_khasara_no"),
        }
        : null,
      startAfter,
      middleFields,
      endFields,
    };
  })();

  /**
   * The step in the Admin's order, or null to draw the default layout below
   * unchanged. The default layout is still built first and the Admin's order
   * sorts it: Mauja, Khasara No., Plot No. and Manual Sub-Khasara become
   * separate cells the Admin placed one by one, the Admin's empty spaces sit
   * between them, and a field the order does not mention keeps its default
   * place after the ones it does.
   */
  const sequencedItems = sequenceOrder
    ? sequenceStepLayout(orderedLayout, sequenceOrder, step.id)
    : null;

  return (
    <FormProvider {...formMethods}>
      <form
        noValidate
        id="add-application-form"
        onSubmit={formMethods.handleSubmit(() => null)}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-10">
          {sequencedItems ? (
            sequencedItems.map((item) =>
              item.kind === "spacer" ? (
                /* An empty space the Admin left between two fields. */
                <div key={item.key} aria-hidden="true" />
              ) : (
                <Fragment key={`field-${item.field.id}`}>
                  <div>
                    <RenderField field={item.field} />
                  </div>
                  {/* The Offline Tentative details follow the Application
                      Type dropdown that switches them on, wherever the Admin
                      placed it. */}
                  {item.field.uid === "rl_application_type" &&
                    showOfflineTentativeDetails && <OfflineTentativeDetailFields />}
                </Fragment>
              )
            )
          ) : (
          <>
          {orderedLayout.startBefore.map((field) => (
            <div key={`field-${field.id}`}>
              <RenderField field={field} />
            </div>
          ))}
          {/* Placed straight after the pinned top fields, so the details sit
              next to the Application Type dropdown that switches them on. */}
          {showOfflineTentativeDetails && <OfflineTentativeDetailFields />}
          {orderedLayout.block && (
            <MaujaKhasaraPlotBlock
              key="mauja-khasara-plot-block"
              maujaField={orderedLayout.block.maujaField}
              khasaraField={orderedLayout.block.khasaraField}
              plotNoField={orderedLayout.block.plotNoField}
              manualKhasaraField={orderedLayout.block.manualKhasaraField}
            />
          )}
          {orderedLayout.startAfter.map((field) => (
            <div key={`field-${field.id}`}>
              <RenderField field={field} />
            </div>
          ))}
          {orderedLayout.middleFields.map((field) => (
            <div key={`field-${field.id}`}>
              <RenderField field={field} />
            </div>
          ))}
          {orderedLayout.endFields.map((field) => (
            <div key={`field-${field.id}`}>
              <RenderField field={field} />
            </div>
          ))}
          </>
          )}
        </div>
        <RenderDocumentConfigs configs={step.documentConfigs || []} />
        {children(formMethods)}
      </form>
    </FormProvider>
  );
}
